import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSeedling, faMicroscope, faGlobeAmericas, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

function Home() {
  // --- REMOVED: The broken scroll listener (useEffect) was here ---

  // Optimized Intersection Observer with single instance
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target); // Stop observing once visible
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('.animate-in');
    elements.forEach((el) => observer.observe(el));
    
    return () => observer.disconnect();
  }, []);

  // --- REMOVED: Unused 'stats' array to fix the warning ---

  const expertise = [
    {
      icon: faSeedling,
      title: 'Farm-to-Export Control',
      description: 'We own and operate our certified farms in Egypt, ensuring complete control from seed to shipment.',
      gradient: 'linear-gradient(135deg, #6bbe44 0%, #5aa838 100%)',
    },
    {
      icon: faMicroscope,
      title: 'Certified Quality',
      description: 'Rigorous quality control with international certifications guarantees purity and freshness in every batch.',
      gradient: 'linear-gradient(135deg, #0073b1 0%, #005a8c 100%)',
    },
    {
      icon: faGlobeAmericas,
      title: 'Global Reach',
      description: 'From Egyptian farms to worldwide markets, our streamlined logistics ensure timely delivery across continents.',
      gradient: 'linear-gradient(135deg, #6bbe44 0%, #0073b1 100%)',
    },
  ];

  const features = [
    { title: 'Our Own Certified Farms', desc: 'Complete traceability and quality from the ground up' },
    { title: 'Integrated Processing', desc: 'State-of-the-art facilities for optimal freshness' },
    { title: 'Direct Export', desc: 'No middlemen, just pure quality from farm to you' },
    { title: 'Transparent Supply Chain', desc: 'Full visibility from cultivation to delivery' },
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section 
        className="hero"
        style={{
          backgroundImage: `
            linear-gradient(to bottom, 
              rgba(0, 20, 35, 0.8) 0%, 
              rgba(0, 77, 115, 0.6) 100%
            ), 
            url('/images/hero.webp')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "scroll"
        }}
      >
        <div className="heroContent">
          <div className="heroLabel">Experience Egyptian Excellence in Herbs & Spices</div>
          <h1 className="heroTitle">
            From Our Own Certified Farms<br />to Global Markets
          </h1>
          <p className="heroSubtitle">
            Rafferty delivers premium-quality herbs and spices with unmatched freshness, purity, and consistency.<br />
            We grow. We process. We export. All in one fully controlled and transparent supply chain.
          </p>
          <div className="heroBrand">Rafferty – Where Quality Begins at the Farm.</div>
          <div className="ctaGroup">
            <Link to="/products" className="ctaButton">
              Explore Our Products
            </Link>
          </div>
        </div>
        
        {/* Simplified gradient orbs */}
        <div className="orb orb1"></div>
        <div className="orb orb2"></div>
        <div className="orb orb3"></div>
      </section>

      {/* Farm Gallery Slider */}
      <FarmGallery />

      {/* Expertise Section */}
      <section className="expertiseSection">
        <div className="animate-in sectionHeader">
          <h2 className="sectionTitle">The Rafferty Advantage</h2>
          <p className="sectionSubtitle">
            From farm ownership to global export, we control every step to deliver exceptional quality
          </p>
        </div>
        
        <div className="expertiseGrid">
          {expertise.map((item, index) => (
            <ExpertiseCard key={index} {...item} index={index} />
          ))}
        </div>
      </section>

      {/* Features Grid */}
      <section className="featuresSection">
        <div className="animate-in sectionHeader">
          <h2 className="sectionTitle">Fully Integrated Operations</h2>
        </div>
        
        <div className="featuresGrid">
          {features.map((feature, index) => (
            <FeatureCard key={index} {...feature} index={index} />
          ))}
        </div>
      </section>
    </div>
  );
}

// Memoized Farm Gallery Component
const FarmGallery = memo(() => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const intervalRef = useRef(null);
  
  const images = [
    { url: '/images/DSC_6487.webp', caption: 'Our Certified Farms' },
    { url: '/images/DSC_6491.webp', caption: 'Quality Cultivation' },
    { url: '/images/DSC_6514.webp', caption: 'Sustainable Farming' },
    { url: '/images/DSC_6533.webp', caption: 'Expert Harvesting' },
    { url: '/images/DSC_6511.webp', caption: 'Farm to Export' }
  ];

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const goToSlide = useCallback((index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  }, []);

  const stopAutoPlay = useCallback(() => {
    setIsAutoPlaying(false);
  }, []);

  // Auto-play with cleanup
  useEffect(() => {
    if (!isAutoPlaying) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    
    intervalRef.current = setInterval(nextSlide, 5000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isAutoPlaying, nextSlide]);

  return (
    <section className="gallerySection">
      <div className="animate-in galleryHeader">
        <h2 className="sectionTitle">Our Farms in Egypt</h2>
        <p className="sectionSubtitle">
          Where quality begins – explore our certified farms and sustainable cultivation practices
        </p>
      </div>

      <div className="sliderContainer">
        <div className="sliderWrapper">
          {/* Main Image */}
          <div className="mainImageContainer">
            <img 
              src={images[currentIndex].url} 
              alt={images[currentIndex].caption}
              className="mainImage"
              loading="lazy"
            />
            <div className="imageOverlay">
              <div className="imageCaption">{images[currentIndex].caption}</div>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button 
            onClick={prevSlide} 
            className="navButton left"
            onMouseEnter={stopAutoPlay}
            aria-label="Previous image"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <button 
            onClick={nextSlide} 
            className="navButton right"
            onMouseEnter={stopAutoPlay}
            aria-label="Next image"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>

        
          {/* Progress Indicators */}
          <div className="indicators">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`indicator ${currentIndex === index ? 'active' : ''}`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
});

FarmGallery.displayName = 'FarmGallery';

// Memoized Expertise Card Component
const ExpertiseCard = memo(({ icon, title, description, gradient, index }) => {
  return (
    <div 
      className="animate-in expertise-card"
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="cardIconBg" style={{ background: gradient }}>
        <FontAwesomeIcon icon={icon} className="cardIcon" />
      </div>
      <h3 className="cardTitle">{title}</h3>
      <p className="cardDescription">{description}</p>
    </div>
  );
});

ExpertiseCard.displayName = 'ExpertiseCard';

// Memoized Feature Card Component
const FeatureCard = memo(({ title, desc, index }) => {
  return (
    <div 
      className="animate-in feature-card"
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="featureIconContainer">
        <div className="featureCheck">✓</div>
      </div>
      <h3 className="featureTitle">{title}</h3>
      <p className="featureDesc">{desc}</p>
    </div>
  );
});

FeatureCard.displayName = 'FeatureCard';

export default Home;